import React, { createContext, useContext, useReducer, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import OIDCAuthService from '../services/OIDCAuthService';
import { handleRoleBasedRouting, getPrimaryRole } from '../utility/roleRoutingUtils';

// Auth state reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload, error: null };
    case 'SET_USER':
      return { 
        ...state, 
        user: action.payload, 
        isAuthenticated: !!action.payload,
        isLoading: false,
        error: null 
      };
    case 'SET_ERROR':
      return { 
        ...state, 
        error: action.payload, 
        isLoading: false 
      };
    case 'LOGOUT':
      return { 
        ...state, 
        user: null, 
        isAuthenticated: false, 
        isLoading: false,
        error: null 
      };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
};

// Initial auth state
const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null
};

// Create Auth Context
const AuthContext = createContext({
  ...initialState,
  login: () => {},
  logout: () => {},
  refreshUser: () => {},
  clearError: () => {}
});

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const history = useHistory();
  const authParams = useSelector(state => state.authParams);

  // Initialize authentication on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        
        // Validate session with backend
        const userData = await OIDCAuthService.validateSession();
        console.log('Session validation result:', userData ? 'User authenticated' : 'User not authenticated');
        if (userData) {
          dispatch({ type: 'SET_USER', payload: userData.user });
          // Handle role-based routing using utility function
          handleRoleBasedRouting(userData.user, history.push);
        } else {
          console.log('User data is null - dispatching LOGOUT');
          dispatch({ type: 'LOGOUT' });
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        dispatch({ type: 'LOGOUT' });
      }
    };

    initializeAuth();
  }, [history]);

  // Login function
  const login = async (intendedRole = null) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      // Extract intended role from redirectUri if present
      const role = intendedRole;
      
      // Get client_id and institute_id from Redux store or sessionStorage
      const clientId = authParams?.client_id || sessionStorage.getItem('client_id');
      const instituteId = authParams?.institute_id || sessionStorage.getItem('institute_id');
      
      // OIDCAuthService.initiateLogin() handles the redirect internally
      await OIDCAuthService.initiateLogin(role, clientId, instituteId);
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  // Register function
  const register = async (intendedRole = null) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      // Extract intended role from redirectUri if present
      const role = intendedRole;
      
      // Get client_id and institute_id from Redux store or sessionStorage
      const clientId = authParams?.client_id || sessionStorage.getItem('client_id');
      const instituteId = authParams?.institute_id || sessionStorage.getItem('institute_id');
      
      await OIDCAuthService.initiateRegister(role, clientId, instituteId);
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  // Logout function
  const logout = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      // Get user role before logout to determine redirect
      const userRole = getPrimaryRole(state.user);
      // Get client_id and institute_id from Redux store (or sessionStorage as fallback)
      const clientId = authParams?.client_id || sessionStorage.getItem('client_id');
      const instituteId = authParams?.institute_id || sessionStorage.getItem('institute_id');
      await OIDCAuthService.logout(userRole, clientId, instituteId);
      dispatch({ type: 'LOGOUT' });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  // Refresh user data
  const refreshUser = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const userData = await OIDCAuthService.validateSession();
      if (userData) {
        dispatch({ type: 'SET_USER', payload: userData.user });
      } else {
        dispatch({ type: 'LOGOUT' });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const value = React.useMemo(() => ({
    ...state,
    login,
    register,
    logout,
    refreshUser,
    clearError,
    setUser: (userData) => dispatch({ type: 'SET_USER', payload: userData }),
    setError: (error) => dispatch({ type: 'SET_ERROR', payload: error }),
    setLoading: (loading) => dispatch({ type: 'SET_LOADING', payload: loading })
  }), [state, login, register, logout, refreshUser, clearError, dispatch]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// PropTypes
AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default AuthContext; 